import { useMemo } from 'react';

import { Grid, Sphere } from '@react-three/drei';
import * as THREE from 'three';

import {
    ANGLE_AXES,
    CanvasScene,
    Club,
    computeAngleDirections,
    DISPLACEMENT_AXES,
    getLockedCameraParams,
    GlobalRotation,
    JOINT_ANGLE_AXES,
    MemoAngleVisualization,
    MemoDisplacementVisualization,
    Skeleton,
    useBallOrigin,
    VISUALIZATION_MEASUREMENT_TYPES,
    type VisualizationAxis,
} from '@elva-visualizer';
import { type Data3d } from '@store';
import { colors } from '@ui';

import { SwingGuideCamera } from './SwingGuideCamera';

type SwingGuide3DSceneProps = {
    swingScene: Data3d;
    frame: number;
    axis: VisualizationAxis;
};

export const SwingGuide3DScene = ({ swingScene, frame, axis }: SwingGuide3DSceneProps) => {
    const targetPointName = axis.targetPoints[0];
    const targetPoint = useMemo(() => {
        if (!targetPointName) return null;

        const keypointId = Object.entries(swingScene.keypointIdToName).find(
            ([, name]) => name === targetPointName,
        )?.[0];
        const position = keypointId ? swingScene.skeleton[frame]?.[keypointId] : undefined;

        return position ? new THREE.Vector3(position[1], position[2], position[0]) : null;
    }, [swingScene, frame, targetPointName]);
    const currentFocus = useMemo(
        () => ({ measurement: axis.measurementName, axis: axis.axisIndex }),
        [axis.axisIndex, axis.measurementName],
    );
    const cameraParams = useMemo(
        () =>
            targetPointName
                ? getLockedCameraParams(swingScene, frame, {
                      axisId: axis.id,
                      keypointName: targetPointName,
                      measurementName: axis.measurementName,
                      axisIndex: axis.axisIndex,
                      lock: axis.camera,
                      targetPoints: axis.targetPoints,
                  })
                : null,
        [axis, swingScene, frame, targetPointName],
    );
    const directions = useMemo(
        () => computeAngleDirections(axis.camera, swingScene.measurements, frame),
        [axis.camera, swingScene.measurements, frame],
    );
    const originOffset = useBallOrigin(swingScene.golfBallTranslation);

    return (
        <CanvasScene>
            <SwingGuideCamera axis={axis} cameraParams={cameraParams} targetPoint={targetPoint} />
            <GlobalRotation>
                <group position={originOffset}>
                    <Sphere args={[0.02135]} position={swingScene.golfBallTranslation}>
                        <meshStandardMaterial color={colors.slate[400]} />
                    </Sphere>
                    <Skeleton frame={frame} {...swingScene} comparisonFrame={null} targetPoints={axis.targetPoints} />
                    <Club frame={frame} clubTrack={swingScene.clubTrack} comparisonFrame={null} />
                    {ANGLE_AXES[axis.id] && (
                        <MemoAngleVisualization
                            type={VISUALIZATION_MEASUREMENT_TYPES[axis.measurementName]}
                            frame={frame}
                            skeleton={swingScene.skeleton}
                            targetPoints={axis.targetPoints}
                            keypointIdToName={swingScene.keypointIdToName}
                            currentFocus={currentFocus}
                            referenceDirection={directions?.referenceDirection}
                            actualDirection={directions?.actualDirection}
                        />
                    )}
                    {JOINT_ANGLE_AXES[axis.id] && (
                        <MemoAngleVisualization
                            type="connected"
                            frame={frame}
                            skeleton={swingScene.skeleton}
                            targetPoints={axis.targetPoints}
                            keypointIdToName={swingScene.keypointIdToName}
                            currentFocus={currentFocus}
                        />
                    )}
                    {DISPLACEMENT_AXES[axis.id] && (
                        <MemoDisplacementVisualization
                            frame={frame}
                            referenceFrame={swingScene.segmentation.P1}
                            targetPoints={axis.targetPoints}
                            skeleton={swingScene.skeleton}
                            keypointIdToName={swingScene.keypointIdToName}
                            currentFocus={currentFocus}
                        />
                    )}
                </group>
            </GlobalRotation>
            <Grid
                infiniteGrid
                fadeFrom={0}
                fadeStrength={2}
                fadeDistance={30}
                cellThickness={1}
                cellSize={0.25}
                cellColor={colors.bluegray[200]}
                sectionColor={colors.bluegray[200]}
            />
        </CanvasScene>
    );
};
